/* eslint-disable modernloop/restrict-imports.cjs */
import React from 'react';

import { logError } from '@modernloop/shared/utils';

import HelpCenterUrl from 'src/constants/HelpCenterUrl';

import GemCard, { Props as GemCardProps } from 'src/views-new/Settings/Integrations/Cards/GemCard';

import { ListIntegrationResponse } from '../api/integration/useIntegrations';

import AtsSevice, { ExtensionSidebarParsedUrlType, ParsedAtsUrlResult, ScorecardGroupByStrategy } from './AtsService';

const getCandidateIdFromUrl = (url: URL): string | null => {
  const folders = url.pathname.split('/') || [];

  const candidateIndex = folders.indexOf('candidate');

  if (candidateIndex > 0) {
    return folders[candidateIndex + 1];
  }

  const candidatesIndex = folders.indexOf('candidates');

  if (candidatesIndex > 0) {
    return folders[candidatesIndex + 1];
  }

  return null;
};

const getApplicationIdFromUrl = (url: URL): string | null => {
  const queryParam = url.searchParams.get('application_id') || null;
  if (queryParam) {
    return queryParam;
  }

  const folders = url.pathname.split('/') || [];
  const applicationIndex = folders.indexOf('applications');

  if (applicationIndex > 0) {
    return folders[applicationIndex + 1];
  }

  return null;
};

/* *
 * GEM ATS service
 * Imp: Never have atsType set in ATS service
 * */
class GemAtsService implements AtsSevice {
  readonly featureFlag = 'user_gem_integration';

  readonly name = 'Gem';

  readonly hostname = 'gem'; // ex. https://www.gem.com/

  readonly logoUrl = `/static/images/integrations/gem.png`;

  readonly helpCenterUrl = HelpCenterUrl.GEM_INTEGRATION;

  readonly scorecardLabelPluralTranslationKey = 'hiring-dashboard-common:common.ats.feedbackForms';

  readonly noScorecardLabelTranslationKey = 'hiring-dashboard-common:common.ats.noFeedbackForm';

  readonly scorecardSelectTitleTranslationKey = 'hiring-dashboard-nav:nav.scorecards.selectTitle';

  readonly scorecardSearchPlaceholderTranslationKey = 'hiring-dashboard-nav:nav.scorecards.searchPlaceholder';

  readonly scorecardSearchPlaceholderEllipsisTranslationKey =
    'hiring-dashboard-nav:nav.scorecards.searchPlaceholderEllipsis';

  readonly isAtsUserSelectEnabled = false;

  readonly isSourcingLinkEnabled = true;

  readonly isAtsImportAvailabilityEnabled = false;

  readonly skipInterviewKitLinkTokenValidation = true;

  readonly scorecardGroupByKey = null;

  readonly scorecardGroupByStrategy = ScorecardGroupByStrategy.AtsJobStage;

  readonly scorecardSortKey = null;

  readonly showEstimatedMinutes = false;

  readonly createCandidatePermissionDialog = null;

  readonly showRequisitionId = true;

  readonly canSupportBrightHire = false;

  readonly hasScorecardSync = false;

  readonly scorecardSyncSettingLabelTranslationKey =
    'hiring-dashboard-common:common.ats.keepInterviewsInSyncWithAtsInterviewPlan';

  readonly hasHiringProcess = false;

  readonly hasScorecards = true;

  readonly hasEnforcedJobPermissions = false;

  readonly resetInterviewPlanWarningMessage =
    'The interview plan for this stage will be reset to its current settings in Gem.';

  // eslint-disable-next-line class-methods-use-this
  getIntegrationCard = (props: GemCardProps, integrations: ListIntegrationResponse): JSX.Element => {
    return <GemCard {...props} gem={integrations?.gem} />;
  };

  parseExtensionSidebarUrl = (externalUrl: string): ParsedAtsUrlResult | null => {
    try {
      const url: URL | undefined = new URL(externalUrl);

      // check if url is gem
      if (!url.hostname.includes(this.hostname)) {
        return null; // exit early
      }

      const encodedCandidateId = getCandidateIdFromUrl(url);
      const decodedCandidateId = encodedCandidateId ? atob(encodedCandidateId) : null;
      const updatedCandidateId = decodedCandidateId?.replace('Person:', 'candidates:');
      const candidateId = btoa(updatedCandidateId || '');

      const applicationId = getApplicationIdFromUrl(url);

      if (candidateId || applicationId) {
        return {
          type: ExtensionSidebarParsedUrlType.Candidate,
          atsCandidateId: candidateId,
          atsApplicationId: applicationId,
        };
      }
    } catch (error) {
      logError('Error parsing Gem ATS URL', error);
    }

    return null;
  };
}

export default new GemAtsService();
